import type { JSX } from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Button, Tooltip } from '@heroui/react';

import { useCloneToProject } from '~/hooks/useJira';
import type { JiraIssue } from '~/services/api/jira';
import { usePRDStore } from '~/stores/prdStore';
import { useUIStore } from '~/stores/uiStore';

interface CloneToProjectButtonProps {
  issue: JiraIssue;
  iconOnly?: boolean;
}

/**
 * Button to clone a Jira issue to a new Compounds project.
 * Shows loading state during clone. Hook handles toast notifications.
 * Component callback handles auto-redirect when mounted.
 */
export function CloneToProjectButton({ iconOnly, issue }: CloneToProjectButtonProps): JSX.Element {
  const navigate = useNavigate();
  const { setActiveSession } = usePRDStore();
  const {
    cloningJiraIssueKey,
    closeJiraTicketModal,
    setActiveProjectAssistantTab,
    setProjectAssistantExpanded,
  } = useUIStore();

  // Hook handles: spinner state (onMutate/onSettled), toasts (onSuccess/onError)
  const cloneMutation = useCloneToProject();

  const handleClone = useCallback(() => {
    cloneMutation.mutate(
      {
        issue_key: issue.key,
        summary: issue.summary,
        description: issue.description,
        status: issue.status,
        priority: issue.priority,
        labels: issue.labels,
        comments: issue.comments,
      },
      {
        // Only fires if component still mounted - handles navigation/modal
        onSuccess: result => {
          closeJiraTicketModal();

          if (result.prd_session_id) {
            setActiveSession(result.project_id, result.prd_session_id);
            setProjectAssistantExpanded(true);
            setActiveProjectAssistantTab('chat');
          }

          void navigate(result.redirect_url);
        },
      },
    );
  }, [
    cloneMutation,
    closeJiraTicketModal,
    issue,
    navigate,
    setActiveProjectAssistantTab,
    setActiveSession,
    setProjectAssistantExpanded,
  ]);

  const isCloning = cloningJiraIssueKey === issue.key;

  const button = (
    <Button
      aria-label={`Clone ${issue.key} to project`}
      color={iconOnly ? 'default' : 'primary'}
      isIconOnly={iconOnly}
      isDisabled={isCloning}
      isLoading={isCloning}
      onPress={handleClone}
      startContent={!isCloning && <DocumentDuplicateIcon className="w-4 h-4" />}
      variant={iconOnly ? 'light' : 'solid'}
      radius={iconOnly ? 'full' : 'md'}
    >
      {!iconOnly && 'Clone to Project'}
    </Button>
  );

  return iconOnly ? (
    <Tooltip content="Clone to Project" delay={500} placement="left">
      {button}
    </Tooltip>
  ) : (
    button
  );
}
